import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getUrl } from 'aws-amplify/storage';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useUserContext } from '../../contexts/UserContext';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { BackButton, Btn } from '../ui';
import { PageHeader } from '../ui/headers';
import '../../css/SingleUnitContent.css';
import { EquipmentCategoryFormCard } from './EquipmentCategoryFormCard';
import { EquipmentUnsavedExitModal } from './EquipmentFormShared';
import { UnsavedChangesBar } from './UnsavedChangesBar';
import { saveNewEquipmentCategory, updateEquipmentCategory } from './equipmentCategorySave';
import { getEquipmentCategoryEditPath } from './equipmentConstants';
import { getEquipmentCategoryNameConflictMessage } from './equipmentUnitNameUtils';

export const EquipmentCategoryEditLegacyRedirect = () => {
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('editId');

    if (!editId) {
        return <Navigate to="/equipment" replace />;
    }

    return <Navigate to={getEquipmentCategoryEditPath(editId)} replace />;
};

export const EquipmentCategoryFormContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { categoryId: editCategoryId } = useParams<{ categoryId?: string }>();
    const { companyGroupedEquipmentData, addCategory, updateCategory, refreshEquipment } = useEquipmentContext();
    const { company, companyOwner } = useUserContext();
    const { registerGuard, tryNavigate, confirmPendingNavigation, cancelPendingNavigation } = useNavigationGuard();

    const isCategoryEditMode = Boolean(editCategoryId);

    const [categoryName, setCategoryName] = useState('');
    const [categoryImage, setCategoryImage] = useState<File | null>(null);
    const [categoryImageUrl, setCategoryImageUrl] = useState('');
    const [categoryImagePreviewFromFile, setCategoryImagePreviewFromFile] = useState('');
    const [categoryImageRemoved, setCategoryImageRemoved] = useState(false);
    const [isSavingCategory, setIsSavingCategory] = useState(false);
    const [categorySaveError, setCategorySaveError] = useState<string | null>(null);
    const [showCreationExitModal, setShowCreationExitModal] = useState(false);

    const existingCategoryForCreation = useMemo(
        () =>
            isCategoryEditMode && editCategoryId
                ? companyGroupedEquipmentData.find((c) => c.id === editCategoryId)
                : null,
        [isCategoryEditMode, editCategoryId, companyGroupedEquipmentData],
    );

    const hasCategoryCreationUnsavedChanges = useMemo(() => {
        return isCategoryEditMode
            ? categoryName.trim() !== (existingCategoryForCreation?.name || '') ||
                  !!categoryImage ||
                  categoryImageRemoved
            : !!categoryName.trim() || !!categoryImage || categoryImageRemoved;
    }, [
        isCategoryEditMode,
        categoryName,
        categoryImage,
        categoryImageRemoved,
        existingCategoryForCreation,
    ]);

    useEffect(() => {
        if (!categoryImage) {
            setCategoryImagePreviewFromFile('');
            return;
        }
        const url = URL.createObjectURL(categoryImage);
        setCategoryImagePreviewFromFile(url);
        return () => URL.revokeObjectURL(url);
    }, [categoryImage]);

    useEffect(() => {
        registerGuard(
            hasCategoryCreationUnsavedChanges ? { when: true, onBlocked: () => setShowCreationExitModal(true) } : null,
        );
        return () => registerGuard(null);
    }, [hasCategoryCreationUnsavedChanges, registerGuard]);

    useEffect(() => {
        const legacyEditId = searchParams.get('editId');
        if (location.pathname === '/equipment/category/new' && legacyEditId) {
            navigate(getEquipmentCategoryEditPath(legacyEditId), { replace: true });
        }
    }, [location.pathname, searchParams, navigate]);

    useEffect(() => {
        if (isCategoryEditMode && editCategoryId) {
            const existingCategory = companyGroupedEquipmentData.find((c) => c.id === editCategoryId);
            if (existingCategory) {
                setCategoryName(existingCategory.name || '');
                if (existingCategory.image) {
                    getUrl({ path: existingCategory.image })
                        .then((result) => setCategoryImageUrl(result.url.href))
                        .catch(() => setCategoryImageUrl(''));
                }
            }
        }
    }, [isCategoryEditMode, editCategoryId, companyGroupedEquipmentData]);

    const companyId = (company?.id as string) || companyOwner?.companyId || '';

    const handleSaveCategory = async () => {
        if (!categoryName.trim()) {
            setCategorySaveError('Equipment category name is required.');
            return;
        }
        if (!companyId) {
            setCategorySaveError('Company context is not available.');
            return;
        }

        const conflictMessage = getEquipmentCategoryNameConflictMessage(
            categoryName,
            companyGroupedEquipmentData,
            isCategoryEditMode ? editCategoryId : null,
        );
        if (conflictMessage) {
            setCategorySaveError(conflictMessage);
            return;
        }

        setCategorySaveError(null);
        setIsSavingCategory(true);

        try {
            if (isCategoryEditMode && editCategoryId) {
                const updatedCategory = await updateEquipmentCategory({
                    companyId,
                    categoryId: editCategoryId,
                    name: categoryName,
                    imageFile: categoryImage,
                    imageRemoved: categoryImageRemoved,
                });
                updateCategory(editCategoryId, updatedCategory);
                await refreshEquipment();
                registerGuard(null);
                navigate('/equipment');
            } else {
                const newCategory = await saveNewEquipmentCategory({
                    companyId,
                    name: categoryName,
                    imageFile: categoryImage,
                });
                addCategory(newCategory);
                await refreshEquipment();
                registerGuard(null);
                navigate(`/equipment/new?categoryId=${encodeURIComponent(newCategory.id)}`);
            }
        } catch (error) {
            console.error('Error saving equipment category:', error);
            const message =
                (error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
                (error instanceof Error ? error.message : null);
            setCategorySaveError(message || 'Failed to save equipment category. Please try again.');
        } finally {
            setIsSavingCategory(false);
        }
    };

    const existingCategory = existingCategoryForCreation;
    const categoryFormImagePreviewUrl =
        categoryImagePreviewFromFile || (categoryImageUrl && !categoryImageRemoved ? categoryImageUrl : '');
    const hasEditChanges = isCategoryEditMode
        ? categoryName.trim() !== (existingCategory?.name || '') ||
          !!categoryImage ||
          categoryImageRemoved
        : true;

    const pageTitle = isCategoryEditMode ? 'Edit Equipment Category' : 'New Equipment Category';
    const pageSubtitle = isCategoryEditMode
        ? 'Update equipment category details below'
        : 'Fill in details below for a new equipment category';

    return (
        <div className="page-container new-category-page">
            <PageHeader
                as="h1"
                title={pageTitle}
                signalLine={pageSubtitle}
                breadcrumbs={[
                    { label: 'Equipment', href: '/equipment' },
                    {
                        label: isCategoryEditMode ? 'Edit Equipment Category' : 'New Equipment Category',
                        href: undefined,
                    },
                ]}
            />
            <UnsavedChangesBar dirty={hasEditChanges} variant="standalone">
                <span className="unsaved-changes-text" style={{ color: 'white' }}>
                    {isCategoryEditMode
                        ? hasEditChanges
                            ? 'Unsaved changes'
                            : `Editing ${existingCategory?.name || 'Equipment Category'}`
                        : 'New equipment category — fill in details below'}
                </span>
                {categorySaveError && <span className="unsaved-changes-error">{categorySaveError}</span>}
                <div className="unsaved-changes-actions">
                    <Btn variant="secondary" buttonText="Cancel" buttonOnClick={() => tryNavigate('/equipment')} />
                    <Btn
                        variant="ghost"
                        buttonText={isSavingCategory ? 'Saving...' : 'Save'}
                        buttonDisabled={isSavingCategory}
                        buttonOnClick={handleSaveCategory}
                    />
                </div>
            </UnsavedChangesBar>

            <BackButton fallbackTo="/equipment" />

            <EquipmentCategoryFormCard
                isCategoryEditMode={isCategoryEditMode}
                categoryName={categoryName}
                onCategoryNameChange={setCategoryName}
                categoryImagePreviewUrl={categoryFormImagePreviewUrl}
                onCategoryImageSelect={(file) => {
                    setCategoryImage(file);
                    setCategoryImageRemoved(false);
                }}
                onCancel={() => tryNavigate('/equipment')}
                onSave={handleSaveCategory}
                isSaving={isSavingCategory}
            />

            <EquipmentUnsavedExitModal
                visible={showCreationExitModal}
                onDismiss={() => {
                    setShowCreationExitModal(false);
                    cancelPendingNavigation();
                }}
                onConfirmLeave={() => {
                    setShowCreationExitModal(false);
                    confirmPendingNavigation();
                }}
            />
        </div>
    );
};
